import { http } from '@/lib/apiClient';
import type {
  CreatePreventivoInput,
  InviaPreventivoInput,
  Paginated,
  PreventivoAnteprima,
  PreventivoCedolinoIncluso,
  PreventivoConfig,
  PreventivoConRelazioni,
  PreventivoFilters,
  PreventivoListItem,
  UpdatePreventivoInput,
  UUID,
} from '@/types';

const BASE = '/preventivi';

export const preventiviService = {
  list(filters: PreventivoFilters = {}): Promise<Paginated<PreventivoListItem>> {
    return http.get<Paginated<PreventivoListItem>>(BASE, { params: filters });
  },

  get(id: UUID): Promise<PreventivoConRelazioni> {
    return http.get<PreventivoConRelazioni>(`${BASE}/${id}`);
  },

  eligibili(clienteId: UUID): Promise<PreventivoCedolinoIncluso[]> {
    return http.get<PreventivoCedolinoIncluso[]>(`${BASE}/eligibili`, { params: { clienteId } });
  },

  anteprima(input: CreatePreventivoInput): Promise<PreventivoAnteprima> {
    return http.post<PreventivoAnteprima, CreatePreventivoInput>(`${BASE}/anteprima`, input);
  },

  create(input: CreatePreventivoInput): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni, CreatePreventivoInput>(BASE, input);
  },

  update(id: UUID, input: UpdatePreventivoInput): Promise<PreventivoConRelazioni> {
    return http.patch<PreventivoConRelazioni, UpdatePreventivoInput>(`${BASE}/${id}`, input);
  },

  rigenera(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/rigenera-pdf`);
  },

  invia(id: UUID, input: InviaPreventivoInput = {}): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni, InviaPreventivoInput>(`${BASE}/${id}/invia`, input);
  },

  inviaEmail(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/invia-email`);
  },

  converti(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/converti`);
  },

  convertiFattura(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/converti-fattura`);
  },

  annulla(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/annulla`);
  },

  accetta(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/accetta`);
  },

  contesta(id: UUID, motivo: string): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni, { motivo: string }>(`${BASE}/${id}/contesta`, { motivo });
  },

  bulkInviaMensili(input: { periodo_mese?: string; invia_email?: boolean } = {}): Promise<{
    inviato: number;
    items: PreventivoConRelazioni[];
  }> {
    return http.post(`${BASE}/mensili/invia-tutti`, input);
  },

  daInviareCount(): Promise<{ count: number }> {
    return http.get<{ count: number }>(`${BASE}/mensili/da-inviare-count`);
  },

  pdf(id: UUID): Promise<Blob> {
    return http.get<Blob>(`${BASE}/${id}/pdf`, { responseType: 'blob' });
  },

  cestino(filters: PreventivoFilters = {}): Promise<Paginated<PreventivoListItem>> {
    return http.get<Paginated<PreventivoListItem>>(`${BASE}/cestino`, { params: filters });
  },

  trash(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}`);
  },

  restore(id: UUID): Promise<PreventivoConRelazioni> {
    return http.post<PreventivoConRelazioni>(`${BASE}/${id}/ripristina`);
  },

  purge(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}/definitivo`);
  },

  getConfig(): Promise<PreventivoConfig> {
    return http.get<PreventivoConfig>(`${BASE}/config`);
  },

  updateConfig(input: PreventivoConfig): Promise<PreventivoConfig> {
    return http.put<PreventivoConfig, PreventivoConfig>(`${BASE}/config`, input);
  },
};
